import React, {
  MouseEvent,
  MouseEventHandler,
  ReactNode,
  useCallback,
} from 'react';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import { ModalProps } from '@mui/material/Modal';
import Dialog, { DialogProps } from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent, { DialogContentProps } from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import LoadingButton from '../LoadingButton';
import { ModalCloseHandler } from './types';

type Props = Omit<DialogProps, 'onClose'> & {
  icon?: ReactNode;
  title: string;
  text?: string;
  contentProps?: DialogContentProps;
  onClose?: ModalCloseHandler;
  onCancel?: MouseEventHandler;
  onConfirm?: MouseEventHandler;
  closeOnConfirm?: boolean;
  cancelBtnLoading?: boolean;
  confirmBtnLoading?: boolean;
  cancelBtnText?: string;
  confirmBtnText?: string;
  showConfirmBtn?: boolean;
  showCancelBtn?: boolean;
};

function ModalDialog({
  children,
  open,
  icon,
  title,
  text,
  contentProps,
  onCancel,
  onClick,
  onClose,
  onConfirm,
  cancelBtnLoading,
  confirmBtnLoading,
  closeOnConfirm,
  cancelBtnText = 'Cancel',
  confirmBtnText = 'Ok',
  showCancelBtn = true,
  showConfirmBtn = true,
  ...rest
}: Props) {
  const showActions = showCancelBtn || showConfirmBtn;

  const handleConfirm = useCallback(
    (event: MouseEvent) => {
      if (closeOnConfirm && onClose) {
        onClose(event);
      }
      if (onConfirm) {
        onConfirm(event);
      }
    },
    [closeOnConfirm, onConfirm, onClose],
  );

  return (
    <Dialog
      open={open}
      onClose={onClose as ModalProps['onClose']}
      onClick={onClick}
      data-testid="modal-dialog"
      {...rest}
    >
      <DialogTitle data-testid="modal-dialog-title">
        {icon && <Avatar sx={{ bgcolor: 'secondary.main' }}>{icon}</Avatar>}
        <Box display="inline" flex={1}>
          {title}
        </Box>
        <IconButton
          data-testid="modal-dialog-close-btn"
          onClick={onClose}
          size="small"
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent {...contentProps}>
        {text}
        {children}
      </DialogContent>
      {showActions && (
        <DialogActions data-testid="modal-dialog-actions">
          {showCancelBtn && (
            <LoadingButton
              loading={cancelBtnLoading}
              onClick={onCancel || onClose}
              data-testid="modal-dialog-cancel-btn"
              variant="outlined"
            >
              {cancelBtnText}
            </LoadingButton>
          )}
          {showConfirmBtn && (
            <LoadingButton
              loading={confirmBtnLoading}
              onClick={handleConfirm}
              data-testid="modal-dialog-confirm-btn"
              autoFocus
            >
              {confirmBtnText}
            </LoadingButton>
          )}
        </DialogActions>
      )}
    </Dialog>
  );
}

export default ModalDialog;
